import * as types from './types';
import api from 'services/api';
import { CURRENT_API_VERSION } from 'services/api/connector';
import * as FileSaver from 'file-saver';
import get from 'lodash/get';
import set from 'lodash/set';
import axios, { CancelToken } from 'axios';
import {
  selectContextMenuXPos,
  selectContextMenuYPos,
  selectCredentialClientEndpoint,
  selectCredentialId,
  selectCredentialLeakDate,
  selectCredentialLinkedVulns,
  selectCredentialOwned,
  selectCredentialPassword,
  selectCredentialsList,
  selectCredentialsSelected,
  selectCredentialUsername,
  selectModalBulkUpdateField,
  selectModalBulkUpdateValue
} from './selectors';
import { getWorkspaces } from 'Screens/Contextualization/Workspaces/actions/Actions';
import {
  selectAdvancedFilterQueryParam,
  selectPage,
  selectQueryParam,
  selectRowsPerPage
} from 'store/Filters/selectors';
import { selectCurrentWorkspace } from 'store/Faraday/selectors';
import { setFilterError, setOrderBy, setPageNumber } from 'store/Filters/actions';
import { selectVulnDetailId } from 'store/Manage/selectors';
import { closeModal, openModal } from 'store/modals/actions';
import { MODAL_MANAGE_BULK_UPDATE, MODAL_MANAGE_BULK_UPDATE_CONFIRMATION } from 'store/modals/modals';

export const clearError = () => async (dispatch) =>
  dispatch({ type: types.CREDENTIALS_CLEAR_ERROR });
export const showCreateEditModal = (show, credential) => (dispatch) =>
  dispatch({ type: types.SHOW_CREATE_EDIT_MODAL, show, credential });
export const setCreateCredentialError = (error) => (dispatch) =>
  dispatch({ type: types.SET_ERROR_CREATE_CREDENTIAL, error });
export const showImportModal = (show) => (dispatch) =>
  dispatch({ type: types.SHOW_CREDENTIAL_IMPORT, show });
export const setCredentialSelected = (credential) => (dispatch) =>
  dispatch({ type: types.INDIVIDUAL_SELECTION_CREDENTIALS, credential });
export const multipleSelectionCredentials = (credential) => (dispatch) =>
  dispatch({ type: types.MULTIPLE_SELECTION_CREDENTIALS, credential });
export const credentialsUnselected = (credential) => (dispatch) =>
  dispatch({ type: types.CREDENTIALS_UNSELECTED, credential });

export const unselectAllCredentials = () => (dispatch) =>
  dispatch({ type: types.CREDENTIALS_UNSELECT_ALL });

export const selectAllCredentials = () => (dispatch, getState) => {
  const credentialsList = selectCredentialsList(getState());
  dispatch({ type: types.CREDENTIALS_SELECT_ALL, credentialsList });
};

export const setCredentialField = (fieldName, value) => (dispatch) => {
  dispatch({ type: types.SET_CREDENTIAL_FIELD, field: fieldName, value });
};

export const getCredentials = () => async (dispatch, getState) => {
  dispatch({ type: types.CREDENTIALS_FETCHING });
  const queryParam = selectQueryParam('credentials', getState());
  try {
    const workspace = selectCurrentWorkspace(getState());
    const response = await api.credentials.getCredentials(workspace, queryParam);
    dispatch({
      type: types.CREDENTIALS_GET_SUCCESS,
      credentials: get(response, 'rows', []),
      count: get(response, 'count', 0)
    });
  } catch (e) {
    return dispatch({
      type: types.CREDENTIALS_GET_FAIL,
      errorMessage: get(e, 'message', 'There was an error, please try again')
    });
  }
};

export const deleteCredentials = () => async (dispatch, getState) => {
  try {
    const workspace = selectCurrentWorkspace(getState());
    const seletedCredentials = selectCredentialsSelected(getState());
    const idsToDelete = {
      ids: seletedCredentials.map((cred) => cred.id)
    };
    const response = await api.credentials.deleteCredentials(workspace, idsToDelete);

    dispatch({
      type: types.CREDENTIALS_GET_SUCCESS,
      credentials: get(response, 'rows', [])
    });

    dispatch(unselectAllCredentials());
    dispatch(getCredentials());
  } catch (e) {
    return dispatch({
      type: types.CREDENTIALS_GET_FAIL,
      errorMessage: get(e, 'message', 'There was an error, please try again')
    });
  }
};

export const deleteCredential = (id, isVulnDetail = false) => async (dispatch, getState) => {
  try {
    const workspace = selectCurrentWorkspace(getState());
    const idsToDelete = {
      ids: [id]
    };
    const response = await api.credentials.deleteCredentials(workspace, idsToDelete);

    dispatch({
      type: types.CREDENTIALS_GET_SUCCESS,
      credentials: get(response, 'rows', [])
    });

    if (isVulnDetail) {
      dispatch(getCredentialsByVuln());
    } else {
      dispatch(unselectAllCredentials());
      dispatch(getCredentials());
    }
  } catch (e) {
    return dispatch({
      type: types.CREDENTIALS_GET_FAIL,
      errorMessage: get(e, 'message', 'There was an error, please try again')
    });
  }
};

export const getCredentialsByVuln = () => async (dispatch, getState) => {
  dispatch({ type: types.CREDENTIALS_FETCHING });
  try {
    const vulnId = selectVulnDetailId(getState());
    const workspace = selectCurrentWorkspace(getState());
    const page = selectPage('credentials', getState());
    const rowsPerPage = selectRowsPerPage('credentials', getState());
    const offset = (page - 1) * rowsPerPage;
    const response = await api.credentials.getCredentialsByVuln(
      workspace,
      vulnId,
      offset,
      rowsPerPage
    );
    dispatch({
      type: types.CREDENTIALS_GET_SUCCESS,
      credentials: get(response, 'rows', []),
      count: get(response, 'count', 0)
    });
  } catch (e) {
    return dispatch({
      type: types.CREDENTIALS_GET_FAIL,
      errorMessage: get(e, 'message', 'There was an error, please try again')
    });
  }
};

export const getAvailableVulns = (search, setDropdown) => async (dispatch, getState) => {
  dispatch({ type: types.SET_AVAILABLE_VULNS_FETCHING });
  try {
    const workspace = selectCurrentWorkspace(getState());
    const queryParam = { filters: [{ name: 'name', op: 'ilike', val: `%${search}%` }] };
    const response = await api.credentials.getAvailableVulns(
      workspace,
      queryParam);
    setDropdown(get(response, 'vulnerabilities', []));
    dispatch({
      type: types.SET_AVAILABLE_VULNS,
      availableVulns: get(response, 'vulnerabilities', [])
    });
  } catch (e) {
    return dispatch({
      type: types.CREDENTIALS_GET_FAIL,
      errorMessage: get(e, 'message', 'There was an error, please try again')
    });
  }
};

export const saveCredential = (isVulnDetail) => async (dispatch, getState) => {
  try {
    const workspaceSelected = selectCurrentWorkspace(getState());
    const credentialsList = selectCredentialsList(getState());
    const id = selectCredentialId(getState());
    const username = selectCredentialUsername(getState());
    const endpoint = selectCredentialClientEndpoint(getState());
    const password = selectCredentialPassword(getState());
    const leak_date = selectCredentialLeakDate(getState());
    const owned = selectCredentialOwned(getState());
    const vulnerabilities = selectCredentialLinkedVulns(getState());
    const vulnId = selectVulnDetailId(getState());

    const data = {
      username,
      endpoint,
      password,
      leak_date,
      owned,
      vulnerabilities: isVulnDetail ? [vulnId] : vulnerabilities.map((vuln) => vuln.id.toString())
    };

    let response = '';
    if (id) {
      response = await api.credentials.editCredential(
        workspaceSelected,
        id,
        data
      );

      const index = credentialsList.findIndex(
        (credential) => id === credential.id
      );

      const updatedCredential = {
        id,
        key: id,
        value: response
      };

      credentialsList[index] = { ...updatedCredential };

      dispatch({
        type: types.CREDENTIAL_UPDATE_SUCCESS,
        updatedCredential,
        credentialsList,
        isVulnDetail
      });
      dispatch(getCredentialsByVuln());
    } else {
      response = await api.credentials.createCredential(
        workspaceSelected,
        data
      );
      if (isVulnDetail) dispatch(getCredentialsByVuln());
      else dispatch(getCredentials());
    }
  } catch (e) {
    dispatch({
      type: types.SAVE_CREDENTIAL_FAIL,
      errorMessage: get(e, 'message', 'There was an error, please try again')
    });
  }
};

export const showCredentialDetail = (show, ws, id) => async (dispatch) => {
  try {
    let data = {};
    if (show) {
      dispatch({ type: types.FETCHING_CREDENTIAL });
      data = await api.credentials.fetchCredential(ws, id);
    }
    dispatch({ type: types.SHOW_CREDENTIAL_DETAIL, show, detail: data || {} });
  } catch (e) {
    dispatch({ type: types.CREDENTIALS_ERROR, error: get(e, 'message', 'There was an error, please try again') });
  }
};

export const updateCredential = (credentialBefore, field, value) => async (dispatch, getState) => {
  try {
    let credentialAfter = { ...credentialBefore };
    set(credentialAfter, field, value);
    const workspaceSelected = selectCurrentWorkspace(getState());
    const credentialsList = selectCredentialsList(getState());
    const data = { [field]: value };

    credentialAfter = await api.credentials.editCredential(
      workspaceSelected,
      credentialBefore.id,
      data
    );

    const index = credentialsList.findIndex(
      (credential) => credentialAfter.id === credential.id
    );

    credentialsList[index] = {
      id: credentialAfter.id,
      key: credentialAfter.id,
      value: credentialAfter
    };

    dispatch({
      type: types.CREDENTIAL_UPDATE_SUCCESS,
      credentialAfter,
      credentialsList
    });
  } catch (e) {
    return dispatch({
      type: types.CREDENTIAL_UPDATE_FAIL,
      error: get(e, 'message', 'There was an error, please try again')
    });
  }
};

export const showContextMenu = (show, XPos, YPos) => {
  return (dispatch, getState) => {
    const state = getState();
    const currentXPos = selectContextMenuXPos(state);
    const currentYPos = selectContextMenuYPos(state);
    const newXPos = XPos ? XPos + 1 : currentXPos;
    const newYPos = YPos ? YPos + 1 : currentYPos;

    dispatch({
      type: types.SHOW_CONTEXT_MENU,
      show,
      contextMenuXPos: newXPos,
      contextMenuYPos: newYPos
    });
  };
};

export const setFilesCount = (files) => (dispatch) =>
  dispatch({ type: types.CREDENTIALS_SET_FILES_TO_UPLOAD, files });

export const setFileIndex = (index) => (dispatch) =>
  dispatch({ type: types.CREDENTIALS_SET_CSV_FILE_INDEX, index });

export const setUploadState = (state) => (dispatch) =>
  dispatch({ type: types.CREDENTIALS_SET_UPLOAD_STATE, state });

export const resetFileUploadState = () => (dispatch) =>
  dispatch({ type: types.CREDENTIALS_RESET_FILE_STATE });

export const uploadFile = (file, isVulnDetail) => async (dispatch, getState) => {
  try {
    dispatch({ type: types.CREDENTIALS_SET_UPLOAD_STATE, state: 'PROCESSING' });
    const ws = selectCurrentWorkspace(getState());
    const vulnId = selectVulnDetailId(getState());
    const response = await api.faraday.getSession();

    const cancelTokenSource = CancelToken.source();

    const fd = new FormData();
    fd.append('csrf_token', response.csrf_token);
    fd.append('file', file);
    if (isVulnDetail) fd.append('vulns_ids', vulnId.toString());

    await axios.post(`/_api/${CURRENT_API_VERSION}/ws/${ws}/credential/import_csv`, fd, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: async (progressEvent) => {
        dispatch({
          type: types.CREDENTIALS_UPLOAD_CSV_UPDATE_PERCENT,
          progressEvent
        });
        if (
          Math.round((100 * progressEvent.loaded) / progressEvent.total) === 100
        ) {
          const index = getState().credentials.fileIndex - 1;
          dispatch({ type: types.CREDENTIALS_SET_CSV_FILE_INDEX, index });
        }
      },
      cancelToken: cancelTokenSource.token
    });

    const currentIndex = getState().credentials.fileIndex;
    if (currentIndex < 0) {
      dispatch({ type: types.CREDENTIALS_SET_UPLOAD_STATE, state: 'FINISHED' });
      dispatch(getWorkspaces());
      if (isVulnDetail) dispatch(getCredentialsByVuln());
      else dispatch(getCredentials());
    } else {
      dispatch({
        type: types.CREDENTIALS_SET_UPLOAD_STATE,
        state: 'PROCESSED'
      });
    }
  } catch (e) {
    if (!e.message || e.message !== 'cancel') {
      const currentIndex = getState().credentials.fileIndex;
      dispatch({
        type: types.CREDENTIALS_SET_NAME_INTO_FILE_ERROR_LIST_CSV,
        name: file.name
      });
      const filesCount = getState().credentials.filesToUpload.length;
      if (filesCount === 1) {
        dispatch({
          type: types.CREDENTIALS_UPLOAD_CSV_ERROR,
          error: "Couldn't upload your file. Please try again."
        });
      }
      if (currentIndex < 0) {
        dispatch({
          type: types.CREDENTIALS_SET_UPLOAD_STATE,
          state: 'FINISHED'
        });
        dispatch(getWorkspaces());
      } else {
        dispatch({
          type: types.CREDENTIALS_SET_UPLOAD_STATE,
          state: 'PROCESSED'
        });
      }
    } else {
      dispatch({ type: types.CREDENTIALS_CANCEL_REQUEST_UPLOAD_CSV });
    }
  }
};

const exportCsv = (result) => async (_, getState) => {
  const workspaceSelected = selectCurrentWorkspace(getState());
  const title = `SR-${workspaceSelected}`;

  const fileType =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const fileExtension = '.csv';

  const data = new Blob([result], { type: fileType });
  FileSaver.saveAs(data, title + fileExtension);
};

export const downloadCredentials = (isVulnDetail = false) => async (dispatch, getState) => {
  const workspace = selectCurrentWorkspace(getState());
  const vulnId = selectVulnDetailId(getState());

  const state = getState();
  let advancedFilterQueryParam = [];
  try {
    advancedFilterQueryParam = selectAdvancedFilterQueryParam(
      state,
      'credentials',
      true
    );
  } catch (e) {
    dispatch(
      setFilterError(
        'credentials',
        'Syntax error. Please try again. For further help check our documentation'
      )
    );
  }

  const hasAdvancedFilter = advancedFilterQueryParam.filters.length > 0;
  const standardQueryParam = selectQueryParam('credentials', state, true);
  const queryParam = hasAdvancedFilter
    ? advancedFilterQueryParam
    : standardQueryParam;

  const downloadQueryParam = isVulnDetail ? { filters: [{ name: 'vulnerabilities', op: 'any', val: { name: 'id', op: 'eq', val: vulnId } }] } : queryParam;

  try {
    dispatch({ type: types.CREDENTIALS_EXPORT_CSV_REQUEST });
    const response = await api.credentials.exportCSV(workspace, downloadQueryParam);
    dispatch(exportCsv(response));
  } catch (err) {
    dispatch({ type: types.CREDENTIALS_EXPORT_CSV_FAILURE });
  }

  return dispatch({ type: types.CREDENTIALS_EXPORT_CSV_SUCCESS });
};

export const setBulkUpdateField = (field) => (dispatch) => dispatch({ type: types.CREDENTIALS_SET_BULK_UPDATE_FIELD, field });

export const setBulkUpdateValue = (value) => (dispatch) => dispatch({ type: types.CREDENTIALS_SET_BULK_UPDATE_VALUE, value });

export const addBulkUpdateValue = (value) => (dispatch) => dispatch({ type: types.CREDENTIALS_ADD_BULK_UPDATE_VALUE, value });

export const removeBulkUpdateValue = (value) => (dispatch) => dispatch({ type: types.CREDENTIALS_REMOVE_BULK_UPDATE_VALUE, value });

export const refreshCredentialsList = (credentialsList, credentialsSelected) => (dispatch, getState) => {
  dispatch({
    type: types.CREDENTIALS_REFRESH_LIST, credentialsList, credentialsSelected
  });
};

export function showBulkUpdateModal (bulkUpdateField, bulkUpdateValue) {
  return (dispatch) => {
    dispatch(setBulkUpdateField(bulkUpdateField));
    dispatch(setBulkUpdateValue(bulkUpdateValue));
    dispatch(openModal(MODAL_MANAGE_BULK_UPDATE));
  };
}

export const bulkUpdateCredentials = () => async (dispatch, getState) => {
  const state = getState();
  try {
    const field = selectModalBulkUpdateField(state);
    const value = selectModalBulkUpdateValue(state);
    const workspaceSelected = selectCurrentWorkspace(state);
    const credentialsList = selectCredentialsList(state);
    const credentialsSelected = selectCredentialsSelected(state);
    const credentialIds = credentialsSelected.map((v) => v.id);

    let data = { [field]: value };
    if (field === 'vulnerabilities') data = { vulnerabilities: value.map((v) => v.id) };
    const payload = { ids: credentialIds, ...data };

    await api.credentials.updateCredentials(workspaceSelected, payload);

    const updatedCredentialsList = credentialsList.map((credential) => {
      const isSelected = credentialsSelected.some((selectedCredential) => selectedCredential.id === credential.id);
      if (isSelected && (field === 'vulnerabilities')) {
        const hasLinkedVulns = credential.value?.vulnerabilities?.length > 0;
        if (hasLinkedVulns) return { ...credential, value: { ...credential.value, [field]: credential.value?.vulnerabilities?.length + 1 } };
        else if (!hasLinkedVulns && value?.length > 1) return { ...credential, value: { ...credential.value, [field]: credential.value?.vulnerabilities?.length + value?.length } };
        else return { ...credential, value: { ...credential.value, [field]: value } };
      }
      if (isSelected && (field !== 'vulnerabilities')) return { ...credential, value: { ...credential.value, ...data } };
      return credential;
    });

    const updatedSelectedCredentials = credentialsSelected.map((credential) => ({ ...credential, value: { ...credential.value, ...data } }));

    dispatch({ type: types.CREDENTIALS_SET_BULK_UPDATE_SUCCESS });
    dispatch(refreshCredentialsList(updatedCredentialsList, updatedSelectedCredentials));
    dispatch(closeModal(MODAL_MANAGE_BULK_UPDATE));
    dispatch(closeModal(MODAL_MANAGE_BULK_UPDATE_CONFIRMATION));
    dispatch({ type: types.CREDENTIALS_BULK_UPDATE_FINISHED });
  } catch (e) {
    dispatch(closeModal(MODAL_MANAGE_BULK_UPDATE));
    dispatch(closeModal(MODAL_MANAGE_BULK_UPDATE_CONFIRMATION));
    dispatch({ type: types.CREDENTIALS_ERROR, error: e.message || 'An error occured while updating credentials' });
  }
};

export const showCredentialModalDelete = (show, id) => (dispatch) => {
  dispatch({ type: types.CREDENTIALS_SHOW_DELETE_CONFIRMATION, show, id });
};

export const setPageNumberCredentials = (pageNumber) => (dispatch) => {
  dispatch(setPageNumber('credentials', pageNumber));
  dispatch(getCredentials());
};

export const setOrderByCredentials = (sorting, isVulnDetail) => (dispatch) => {
  dispatch(setOrderBy('credentials', sorting));
  if (isVulnDetail) dispatch(getCredentialsByVuln());
  else dispatch(getCredentials());
};
